/**
 * One-off import of the legacy Replit/Neon Postgres export.
 *
 * The user runs `npm run export:legacy` against their own DATABASE_URL, which
 * writes `dashbase-export.json`. That file is loaded here and pushed through
 * the `migrate.*` mutations table by table, in dependency order — categories
 * before items, quests before goals — because each step resolves the previous
 * step's legacy integer ids into Convex ids.
 *
 * Rows are sent in chunks so a large table cannot exceed a single mutation's
 * limits, and every mutation is idempotent, so a failed run can simply be
 * repeated.
 */
import { useRef, useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { Upload, CheckCircle2, AlertTriangle } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

/** Rows per mutation call. Well inside Convex's per-transaction limits. */
const CHUNK = 200;

interface StepResult {
  table: string;
  inserted: number;
  updated: number;
  skipped: number;
}

export function LegacyImport() {
  const fileRef = useRef<HTMLInputElement>(null);

  const claimLegacyUser = useMutation(api.migrate.claimLegacyUser);
  const importCategories = useMutation(api.migrate.importCategories);
  const importItems = useMutation(api.migrate.importItems);
  const importItemCategories = useMutation(api.migrate.importItemCategories);
  const importTasks = useMutation(api.migrate.importTasks);
  const importFocusSessions = useMutation(api.migrate.importFocusSessions);
  const importFocusLogs = useMutation(api.migrate.importFocusLogs);
  const importQuestCategories = useMutation(api.migrate.importQuestCategories);
  const importQuests = useMutation(api.migrate.importQuests);
  const importGoals = useMutation(api.migrate.importGoals);
  const importNotes = useMutation(api.migrate.importNotes);
  const importTags = useMutation(api.migrate.importTags);
  const importNoteTags = useMutation(api.migrate.importNoteTags);
  const importLists = useMutation(api.migrate.importLists);
  const importListItems = useMutation(api.migrate.importListItems);

  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<StepResult[]>([]);
  const [error, setError] = useState<string | null>(null);

  const run = async (file: File) => {
    setRunning(true);
    setResults([]);
    setError(null);
    setProgress(0);

    try {
      const payload = JSON.parse(await file.text());

      if (payload.legacyUserId) {
        await claimLegacyUser({ legacyUserId: String(payload.legacyUserId) });
      }

      // Order matters: each step resolves ids written by the ones above it.
      const steps: Array<{
        table: string;
        rows: unknown[];
        send: (rows: never[]) => Promise<StepResultLike>;
      }> = [
        { table: "categories", rows: payload.categories ?? [], send: (rows) => importCategories({ rows }) },
        { table: "items", rows: payload.items ?? [], send: (rows) => importItems({ rows }) },
        { table: "item_categories", rows: payload.item_categories ?? [], send: (rows) => importItemCategories({ rows }) },
        { table: "tasks", rows: payload.tasks ?? [], send: (rows) => importTasks({ rows }) },
        { table: "focus_sessions", rows: payload.focus_sessions ?? [], send: (rows) => importFocusSessions({ rows }) },
        { table: "focus_logs", rows: payload.focus_logs ?? [], send: (rows) => importFocusLogs({ rows }) },
        { table: "quest_categories", rows: payload.quest_categories ?? [], send: (rows) => importQuestCategories({ rows }) },
        { table: "quests", rows: payload.quests ?? [], send: (rows) => importQuests({ rows }) },
        { table: "goals", rows: payload.goals ?? [], send: (rows) => importGoals({ rows }) },
        { table: "notes", rows: payload.notes ?? [], send: (rows) => importNotes({ rows }) },
        { table: "tags", rows: payload.tags ?? [], send: (rows) => importTags({ rows }) },
        { table: "note_tags", rows: payload.note_tags ?? [], send: (rows) => importNoteTags({ rows }) },
        { table: "lists", rows: payload.lists ?? [], send: (rows) => importLists({ rows }) },
        { table: "list_items", rows: payload.list_items ?? [], send: (rows) => importListItems({ rows }) },
      ];

      const collected: StepResult[] = [];

      for (const [index, step] of steps.entries()) {
        const totals = { inserted: 0, updated: 0, skipped: 0 };

        for (let offset = 0; offset < step.rows.length; offset += CHUNK) {
          const chunk = step.rows.slice(offset, offset + CHUNK);
          const result = await step.send(chunk as never[]);
          totals.inserted += result.inserted;
          totals.updated += result.updated;
          totals.skipped += result.skipped;
        }

        collected.push({ table: step.table, ...totals });
        setResults([...collected]);
        setProgress(((index + 1) / steps.length) * 100);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setRunning(false);
      // Clear the picker so re-selecting the same file fires onChange again.
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const totalSkipped = results.reduce((sum, r) => sum + r.skipped, 0);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Import legacy data</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-muted-foreground space-y-2">
          <p>
            Brings across bookmarks, dailies, focus history, quests and notes
            from the old Replit deployment.
          </p>
          <ol className="list-decimal list-inside space-y-1 text-xs">
            <li>
              On your own machine, set <code className="font-mono">DATABASE_URL</code>{" "}
              to the old Neon connection string.
            </li>
            <li>
              Run <code className="font-mono">npm run export:legacy</code> — it
              writes <code className="font-mono">data-export/dashbase-export.json</code>.
            </li>
            <li>Load that file here.</li>
          </ol>
          <p className="text-xs">
            Safe to run more than once — rows are matched on their old id and
            updated rather than duplicated.
          </p>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void run(file);
          }}
        />

        <Button
          onClick={() => fileRef.current?.click()}
          disabled={running}
          variant="outline"
        >
          <Upload className="w-4 h-4 mr-1.5" />
          {running ? "Importing…" : "Choose export file"}
        </Button>

        {running && <Progress value={progress} />}

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-destructive" />
            <div className="text-sm min-w-0">
              <p className="font-medium text-destructive">Import stopped</p>
              <p className="text-xs text-muted-foreground break-words">{error}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Anything already imported was kept. Fix the cause and run it
                again — completed rows are skipped.
              </p>
            </div>
          </div>
        )}

        {results.length > 0 && (
          <div className="space-y-2">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-muted-foreground text-left">
                  <th className="font-normal py-1">Table</th>
                  <th className="font-normal py-1 text-right">New</th>
                  <th className="font-normal py-1 text-right">Updated</th>
                  <th className="font-normal py-1 text-right">Skipped</th>
                </tr>
              </thead>
              <tbody className="font-mono tabular-nums">
                {results.map((row) => (
                  <tr key={row.table} className="border-t border-border">
                    <td className="py-1 font-sans">{row.table}</td>
                    <td className="py-1 text-right">{row.inserted}</td>
                    <td className="py-1 text-right">{row.updated}</td>
                    <td
                      className={cn(
                        "py-1 text-right",
                        row.skipped > 0 && "text-amber-400",
                      )}
                    >
                      {row.skipped}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {!running && !error && (
              <div className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                <div>
                  <p>Import finished.</p>
                  {totalSkipped > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {totalSkipped} row{totalSkipped === 1 ? "" : "s"} skipped —
                      these referenced a parent row that was not in the export
                      (for example a goal whose quest was already deleted).
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Shape every `migrate.import*` mutation returns. */
interface StepResultLike {
  inserted: number;
  updated: number;
  skipped: number;
}
